import React, { useCallback, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { CheckCircle2, PackageCheck, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import ParticleService from '../ParticleService';

const emptyQueue = { items: [], total: 0, awaitingCount: 0, reviewCount: 0 };
const statusVariant = status => ({
  AwaitingFulfillment: 'warning', PartiallyFulfilled: 'info', Fulfilled: 'success',
  Revoked: 'secondary', RequiresReview: 'danger', ReviewResolved: 'success',
}[status] || 'secondary');
const formatDate = value => value ? new Date(value).toLocaleDateString() : '—';
const formatDateTime = value => value ? new Date(value).toLocaleString() : '—';
const reviewLabel = disposition => ({
  KeepAccessApproved: 'Keep assigned access',
  ReturnOrReplacementHandled: 'Return or replacement handled separately',
}[disposition] || disposition);

export default function PurchaseFulfillment() {
  const [queue, setQueue] = useState(emptyQueue);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [store, setStore] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(null);
  const [form, setForm] = useState({ customerEmail: '', deviceIdentifier: '', allowCustomerOverride: false, reason: '', disposition: '', acknowledged: false });

  const load = useCallback(async () => {
    setLoading(true);
    const result = await ParticleService.getPurchaseFulfillmentQueue(submittedSearch, status, store);
    if (result.error) toast.error(result.detail || result.error);
    else setQueue(result);
    setLoading(false);
  }, [submittedSearch, status, store]);

  useEffect(() => { load(); }, [load]);

  const openFulfillment = purchase => {
    setAction({ type: 'fulfill', purchase });
    setForm({ customerEmail: purchase.customerEmail || purchase.buyerEmail, deviceIdentifier: '', allowCustomerOverride: false, reason: '', disposition: '', acknowledged: false });
  };

  const openReversal = (purchase, fulfillment) => {
    setAction({ type: 'reverse', purchase, fulfillment });
    setForm({ customerEmail: '', deviceIdentifier: '', allowCustomerOverride: false, reason: '', disposition: '', acknowledged: false });
  };

  const openReconciliation = purchase => {
    setAction({ type: 'reconcile', purchase });
    setForm({ customerEmail: '', deviceIdentifier: '', allowCustomerOverride: false, reason: '', disposition: '', acknowledged: false });
  };

  const openReview = purchase => {
    setAction({ type: 'review', purchase });
    setForm({ customerEmail: '', deviceIdentifier: '', allowCustomerOverride: false, reason: '', disposition: '', acknowledged: false });
  };

  const submit = async event => {
    event.preventDefault();
    if (action.type === 'review' && !form.acknowledged) {
      toast.error('Confirm that the related return, refund, or policy decision has been reviewed.');
      return;
    }
    setSaving(true);
    let result;
    if (action.type === 'fulfill') result = await ParticleService.fulfillPurchase(action.purchase.purchaseId, form);
    else if (action.type === 'reverse') result = await ParticleService.reversePurchaseFulfillment(action.fulfillment.fulfillmentId, form.reason.trim());
    else if (action.type === 'reconcile') result = await ParticleService.reconcilePurchaseAllocation(action.purchase.purchaseId, form.reason.trim());
    else result = await ParticleService.completePurchaseReview(action.purchase.purchaseId, form.disposition, form.reason.trim());
    if (result.error) toast.error(result.detail || result.error);
    else {
      const successMessage = {
        fulfill: 'Device allocation fulfilled.', reverse: 'Fulfillment reversed.',
        reconcile: 'Purchase allocation reconciled.', review: 'Purchase review completed.',
      }[action.type];
      toast.success(successMessage);
      setAction(null);
      await load();
    }
    setSaving(false);
  };

  return (
    <div className="container-fluid p-4">
      <div className="mb-4">
        <h2 className="mb-1 d-flex align-items-center gap-2"><PackageCheck size={25} /> Purchase Fulfillment</h2>
        <p className="text-muted mb-0">Connect paid WooCommerce device allocations to customer-owned Particle devices.</p>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-4">
        <Card className="shadow-sm"><Card.Body><div className="small text-muted">Awaiting devices</div><strong className="fs-4">{queue.awaitingCount}</strong></Card.Body></Card>
        <Card className="shadow-sm"><Card.Body><div className="small text-muted">Requires review</div><strong className="fs-4 text-danger">{queue.reviewCount}</strong></Card.Body></Card>
      </div>

      <Alert variant="info">First claim and assign the device using the Provisioning Dashboard, then refresh Particle ownership. Fulfillment verifies that assignment and reserves the purchase allocation; it does not start the free year.</Alert>

      <Card className="shadow-sm">
        <Card.Header className="bg-white py-3 d-flex flex-wrap align-items-end justify-content-between gap-3">
          <strong>{queue.total} purchase allocation(s)</strong>
          <div className="d-flex flex-wrap gap-2">
            <Form.Select value={store} onChange={event => setStore(event.target.value)} style={{ width: 180 }}>
              <option value="">All stores</option>
              <option value="Canada">Canada</option>
              <option value="UnitedStates">United States</option>
            </Form.Select>
            <Form.Select value={status} onChange={event => setStatus(event.target.value)} style={{ width: 210 }}>
              <option value="">All statuses</option>
              <option value="AwaitingFulfillment">Awaiting fulfillment</option>
              <option value="PartiallyFulfilled">Partially fulfilled</option>
              <option value="Fulfilled">Fulfilled</option>
              <option value="RequiresReview">Requires review</option>
              <option value="ReviewResolved">Review resolved</option>
              <option value="Revoked">Revoked</option>
            </Form.Select>
            <Form onSubmit={event => { event.preventDefault(); setSubmittedSearch(search.trim()); }}>
              <InputGroup><Form.Control value={search} onChange={event => setSearch(event.target.value)} placeholder="Order, buyer, or product" /><Button type="submit" variant="outline-primary"><Search size={17} /></Button></InputGroup>
            </Form>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? <div className="text-center p-5"><Spinner animation="border" /></div> : queue.items.length === 0 ?
            <div className="text-center text-muted p-5">No WooCommerce purchase allocations match this view.</div> :
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light"><tr><th>Order</th><th>Buyer / customer</th><th>Product</th><th>Allocation</th><th>Status</th><th></th></tr></thead>
              <tbody>{queue.items.map(purchase => <tr key={purchase.purchaseId}>
                <td><strong>#{purchase.orderNumber}</strong> <Badge bg={purchase.store === 'UnitedStates' ? 'primary' : 'danger'}>{purchase.store === 'UnitedStates' ? 'U.S.' : 'CA'}</Badge><div className="small text-muted">{formatDate(purchase.orderedAtUtc)} · {purchase.currency}</div></td>
                <td><div>{purchase.buyerEmail}</div><div className="small text-muted">{purchase.customerEmail ? `Account: ${purchase.customerEmail}` : 'No matching account yet'}</div></td>
                <td><strong>{purchase.productName}</strong><div className="small text-muted">Order quantity: {purchase.quantity}</div></td>
                <td><strong>{purchase.fulfilledDeviceCount}/{purchase.includedDeviceCount}</strong><div className="small text-muted">{purchase.remainingDeviceCount} remaining</div>
                  {purchase.devices.map(device => <div className="small border-top mt-2 pt-2" key={device.fulfillmentId}>
                    <span className="font-monospace">{device.serialNumber || device.particleDeviceId}</span> · {device.customerEmail}
                    <Button size="sm" variant="link" className="py-0 text-danger" onClick={() => openReversal(purchase, device)}><RotateCcw size={13} /> Reverse</Button>
                  </div>)}
                </td>
                <td><Badge bg={statusVariant(purchase.status)}>{purchase.status}</Badge>{purchase.reviewedAtUtc && <div className="small mt-2"><CheckCircle2 size={13} className="me-1 text-success" />{reviewLabel(purchase.reviewDisposition)}<div className="text-muted">{purchase.reviewResolution}</div><div className="text-muted">{purchase.reviewedBy} · {formatDateTime(purchase.reviewedAtUtc)}</div></div>}</td>
                <td className="text-end"><div className="d-flex flex-column align-items-end gap-1">
                  {purchase.status === 'RequiresReview' && <Button size="sm" variant="outline-danger" onClick={() => openReview(purchase)}>Resolve review</Button>}
                  {purchase.reconciliationNeeded && <Button size="sm" variant="outline-secondary" onClick={() => openReconciliation(purchase)}><RefreshCw size={13} className="me-1" />Reconcile counts</Button>}
                  <Button size="sm" disabled={purchase.remainingDeviceCount <= 0 || ['Revoked', 'RequiresReview', 'ReviewResolved'].includes(purchase.status)} onClick={() => openFulfillment(purchase)}>Fulfill device</Button>
                </div></td>
              </tr>)}</tbody>
            </Table>}
        </Card.Body>
      </Card>

      <Modal show={action !== null} onHide={() => !saving && setAction(null)} centered>
        <Form onSubmit={submit}>
          <Modal.Header closeButton={!saving}><Modal.Title>{{
            reverse: 'Reverse fulfillment', fulfill: 'Fulfill device allocation',
            reconcile: 'Reconcile purchase allocation', review: 'Resolve purchase review',
          }[action?.type]}</Modal.Title></Modal.Header>
          <Modal.Body>
            {action?.type === 'reverse' ? <>
              <p>Release device <strong className="font-monospace">{action.fulfillment.serialNumber || action.fulfillment.particleDeviceId}</strong> from order #{action.purchase.orderNumber}. This is allowed only before its free year is activated.</p>
            </> : action?.type === 'reconcile' ? <>
              <Alert variant="info">This recalculates the fulfilled count and allocation status from the preserved device links. It does not contact WooCommerce, change device assignment, or change dashboard access.</Alert>
              <p className="mb-0">Reconcile order <strong>#{action.purchase.orderNumber}</strong>?</p>
            </> : action?.type === 'review' ? <>
              <Alert variant="warning">Resolving this review records the decision only. It does not issue a WooCommerce refund, reverse fulfillment, or change the customer's dashboard access.</Alert>
              <div className="mb-3"><strong>Order #{action.purchase.orderNumber}</strong><div className="small text-muted">WooCommerce status: {action.purchase.status} · {action.purchase.fulfilledDeviceCount} fulfilled device(s)</div></div>
              <Form.Group className="mb-3"><Form.Label>Resolution</Form.Label><Form.Select required value={form.disposition} onChange={event => setForm(current => ({ ...current, disposition: event.target.value }))}><option value="">Select an outcome…</option><option value="KeepAccessApproved">Keep assigned access as a policy exception</option><option value="ReturnOrReplacementHandled">Return or replacement handled separately</option></Form.Select></Form.Group>
              <Form.Check className="mb-3" required type="checkbox" checked={form.acknowledged} onChange={event => setForm(current => ({ ...current, acknowledged: event.target.checked }))} label="I reviewed the order and any required return, replacement, or entitlement action has been completed." />
            </> : <>
              <div className="mb-3"><strong>Order #{action?.purchase.orderNumber}</strong><div className="small text-muted">WooCommerce buyer: {action?.purchase.buyerEmail}</div></div>
              <Form.Group className="mb-3"><Form.Label>Customer account email</Form.Label><Form.Control type="email" required value={form.customerEmail} onChange={event => setForm(current => ({ ...current, customerEmail: event.target.value }))} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Particle device ID or serial number</Form.Label><Form.Control required minLength={4} maxLength={128} value={form.deviceIdentifier} onChange={event => setForm(current => ({ ...current, deviceIdentifier: event.target.value }))} /></Form.Group>
              <Form.Check className="mb-3" type="checkbox" label="Allow customer to differ from WooCommerce buyer" checked={form.allowCustomerOverride} onChange={event => setForm(current => ({ ...current, allowCustomerOverride: event.target.checked }))} />
            </>}
            <Form.Group><Form.Label>{action?.type === 'review' ? 'Resolution notes' : 'Reason'}</Form.Label><Form.Control as="textarea" rows={3} required minLength={5} maxLength={action?.type === 'review' ? 1000 : 500} value={form.reason} onChange={event => setForm(current => ({ ...current, reason: event.target.value }))} placeholder="Required for the audit history" /></Form.Group>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" disabled={saving} onClick={() => setAction(null)}>Cancel</Button><Button type="submit" variant={['reverse', 'review'].includes(action?.type) ? 'danger' : 'primary'} disabled={saving}>{saving ? 'Saving…' : {
            reverse: 'Reverse fulfillment', fulfill: 'Fulfill device', reconcile: 'Reconcile allocation', review: 'Complete review',
          }[action?.type]}</Button></Modal.Footer>
        </Form>
      </Modal>
      <ToastContainer />
    </div>
  );
}
